import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { getSupabaseConfig, getSupabaseStorageKeyFromDirectUrl, resilientFetch } from "@/lib/supabase/client";
import { useSupabaseSession } from "@/hooks/use-supabase-session";

type SupportTicketSummary = {
  id: string;
  ticketNo: string;
  subject: string;
  status: "open" | "closed" | "archived";
  createdAt: number;
  updatedAt: number;
  userId: string;
  userEmail: string;
  messagesCount: number;
  lastMessage: string;
  lastActor: "user" | "admin";
};

type SupportMessage = {
  id: string;
  createdAt: number;
  actor: "user" | "admin";
  message: string;
  senderName: string;
};

type SupportTicketDetail = SupportTicketSummary & {
  messages: SupportMessage[];
};

function readAccessTokenFromStorage() {
  try {
    const { directUrl } = getSupabaseConfig();
    const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";
    if (!storageKey) return "";
    const raw = globalThis.localStorage?.getItem(storageKey) ?? "";
    const parsed = raw ? (JSON.parse(raw) as any) : null;
    return typeof parsed?.access_token === "string" ? parsed.access_token : "";
  } catch {
    return "";
  }
}

function authHeaders() {
  const token = readAccessTokenFromStorage();
  if (!token) throw new Error("Lütfen tekrar giriş yapın.");
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

async function fetchTickets() {
  const token = readAccessTokenFromStorage();
  if (!token) throw new Error("Oturum bilgisi bulunamadı. Lütfen tekrar giriş yapın.");

  const res = await resilientFetch("/api/numeroloji/support/tickets", {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Destek talepleri alınamadı.");
  }
  return (Array.isArray(json.items) ? json.items : []) as SupportTicketSummary[];
}

async function fetchTicketDetail(id: string) {
  const token = readAccessTokenFromStorage();
  if (!token) throw new Error("Oturum bilgisi bulunamadı. Lütfen tekrar giriş yapın.");

  const res = await resilientFetch(`/api/numeroloji/support/tickets/${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ticket detayı alınamadı.");
  }
  return json.item as SupportTicketDetail;
}

async function createTicket(input: { subject: string; message: string }) {
  const res = await resilientFetch("/api/numeroloji/support/tickets", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Destek talebi oluşturulamadı.");
  }
  return json.item as SupportTicketDetail | null;
}

async function createGuestTicket(input: { fullName: string; email: string; subject: string; message: string }) {
  const res = await resilientFetch("/api/numeroloji/support/public-ticket", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Destek talebi oluşturulamadı.");
  }
  return json.ticketNo as string;
}

async function replyTicket(input: { id: string; message: string }) {
  const res = await resilientFetch(`/api/numeroloji/support/tickets/${encodeURIComponent(input.id)}/reply`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ message: input.message }),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Yanıt gönderilemedi.");
  }
  return json.item as SupportTicketDetail | null;
}

async function updateTicketStatus(input: { id: string; status: "open" | "closed" | "archived" }) {
  const res = await resilientFetch(`/api/numeroloji/support/tickets/${encodeURIComponent(input.id)}/status`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ status: input.status }),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ticket durumu güncellenemedi.");
  }
  return json.item as SupportTicketDetail | null;
}

async function fetchMembershipSummary() {
  const token = readAccessTokenFromStorage();
  if (!token) return null;

  const res = await resilientFetch("/api/numeroloji/me/entitlements", {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) return null;
  const payload = (await res.json().catch(() => null)) as any;
  return payload?.membership ?? null;
}

function formatDate(ts: number) {
  try {
    return new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(ts * 1000));
  } catch {
    return String(ts);
  }
}

export default function UserPanelDestek() {
  const queryClient = useQueryClient();
  const { user, loading } = useSupabaseSession();
  const [selectedId, setSelectedId] = useState<string>("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [listMode, setListMode] = useState<"active" | "archive">("active");
  const [guestFullName, setGuestFullName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestSuccess, setGuestSuccess] = useState<string | null>(null);

  const ticketsQuery = useQuery({
    queryKey: ["panel-support-tickets", user?.id],
    queryFn: fetchTickets,
    enabled: !loading && Boolean(user?.id),
    staleTime: 5_000,
    retry: false,
  });

  const entitlementsQuery = useQuery({
    queryKey: ["numeroloji-membership-summary", user?.id],
    queryFn: fetchMembershipSummary,
    enabled: !loading && Boolean(user?.id),
    staleTime: 30_000,
    retry: false,
  });

  const hasPrioritySupport = useMemo(() => {
    const raw = entitlementsQuery.data?.hasFeature;
    if (!raw || typeof raw !== "object") return false;
    return Boolean((raw as Record<string, unknown>).priority_support);
  }, [entitlementsQuery.data?.hasFeature]);

  const visibleTickets = useMemo(
    () =>
      (ticketsQuery.data ?? []).filter((ticket) =>
        listMode === "archive" ? ticket.status === "archived" : ticket.status !== "archived",
      ),
    [ticketsQuery.data, listMode],
  );

  useEffect(() => {
    if (!visibleTickets.length) {
      setSelectedId("");
      return;
    }
    if (!selectedId || !visibleTickets.some((item) => item.id === selectedId)) {
      setSelectedId(visibleTickets[0].id);
    }
  }, [visibleTickets, selectedId]);

  const detailQuery = useQuery({
    queryKey: ["panel-support-ticket", selectedId, user?.id],
    queryFn: () => fetchTicketDetail(selectedId),
    enabled: !loading && Boolean(user?.id) && Boolean(selectedId),
    staleTime: 3_000,
    retry: false,
  });

  const createMutation = useMutation({
    mutationFn: async (input: { subject: string; message: string }) => {
      const trimmedSubject = input.subject.trim();
      const normalizedSubject = hasPrioritySupport && !/^\[PRIORITY\]/i.test(trimmedSubject) ? `[PRIORITY] ${trimmedSubject}` : trimmedSubject;
      return await createTicket({
        ...input,
        subject: normalizedSubject,
      });
    },
    onSuccess: async (item) => {
      setSubject("");
      setMessage("");
      await queryClient.invalidateQueries({ queryKey: ["panel-support-tickets"] });
      if (item?.id) {
        setSelectedId(item.id);
        await queryClient.invalidateQueries({ queryKey: ["panel-support-ticket", item.id] });
      }
    },
  });

  const guestMutation = useMutation({
    mutationFn: (input: { fullName: string; email: string; subject: string; message: string }) =>
      createGuestTicket(input),
    onSuccess: (ticketNo) => {
      setGuestFullName("");
      setGuestEmail("");
      setSubject("");
      setMessage("");
      setGuestSuccess(ticketNo);
    },
  });

  const replyMutation = useMutation({
    mutationFn: replyTicket,
    onSuccess: async (item) => {
      setReply("");
      await queryClient.invalidateQueries({ queryKey: ["panel-support-tickets"] });
      if (item?.id) {
        await queryClient.invalidateQueries({ queryKey: ["panel-support-ticket", item.id] });
      }
    },
  });

  const statusMutation = useMutation({
    mutationFn: updateTicketStatus,
    onSuccess: async (item) => {
      await queryClient.invalidateQueries({ queryKey: ["panel-support-tickets"] });
      if (item?.id) {
        await queryClient.invalidateQueries({ queryKey: ["panel-support-ticket", item.id] });
      }
    },
  });

  const selectedTicket = useMemo(() => detailQuery.data ?? null, [detailQuery.data]);

  const isGuest = !loading && !user?.id;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6">
      <header>
        <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">Destek Merkezi</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {isGuest
            ? "Aşağıdaki formu doldurun, destek ekibimiz e-posta ile size ulaşacaktır."
            : "Bir sorun yaşadığında ticket aç, yönetim yanıtladıkça burada gör."}
        </p>
        {hasPrioritySupport ? (
          <div className="mt-3 inline-flex items-center rounded-full bg-[var(--panel-primary)]/15 px-3 py-1 text-xs font-bold text-[var(--panel-primary)]">
            Öncelikli destek aktif (Pro)
          </div>
        ) : null}
      </header>

      {isGuest ? (
        <Card className="p-5">
          {guestSuccess ? (
            <div className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-700">
              <p className="font-semibold">Talebiniz alındı! Ticket No: #{guestSuccess}</p>
              <p className="mt-1 text-emerald-600">En kısa sürede e-posta adresinize yanıt göndereceğiz.</p>
              <button
                type="button"
                className="mt-3 text-xs font-semibold text-emerald-700 underline"
                onClick={() => setGuestSuccess(null)}
              >
                Yeni talep gönder
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-2">
                <Input
                  value={guestFullName}
                  onChange={(e) => setGuestFullName(e.target.value)}
                  placeholder="Ad Soyad *"
                />
                <Input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="E-posta *"
                />
              </div>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Konu *"
                className="mt-3"
              />
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Sorununu detaylı yazabilirsin *"
                className="mt-3 min-h-[110px]"
              />
              <Button
                disabled={guestMutation.isPending}
                onClick={() =>
                  guestMutation.mutate({
                    fullName: guestFullName.trim(),
                    email: guestEmail.trim(),
                    subject: subject.trim(),
                    message: message.trim(),
                  })
                }
                className="mt-3 rounded-xl"
              >
                {guestMutation.isPending ? "Gönderiliyor..." : "Destek Talebi Gönder"}
              </Button>
              {guestMutation.isError ? (
                <p className="mt-2 text-sm text-rose-600">{(guestMutation.error as Error).message}</p>
              ) : null}
            </>
          )}
        </Card>
      ) : (
        <Card className="p-5">
          <div className="grid gap-3 md:grid-cols-2">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Konu" />
            <Button
              disabled={createMutation.isPending}
              onClick={() => createMutation.mutate({ subject: subject.trim(), message: message.trim() })}
              className="rounded-xl"
            >
              {createMutation.isPending ? "Gönderiliyor..." : "Yeni Ticket Oluştur"}
            </Button>
          </div>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Sorununu detaylı yazabilirsin"
            className="mt-3 min-h-[110px]"
          />
          {createMutation.isError ? <p className="mt-2 text-sm text-rose-600">{(createMutation.error as Error).message}</p> : null}
        </Card>
      )}

      {isGuest ? null : <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
        <Card className="p-3">
          <div className="mb-2 px-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Ticket Listesi</div>
          <div className="mb-3 flex gap-2 px-1">
            <Button type="button" variant={listMode === "active" ? "default" : "outline"} className="h-8 rounded-full px-3 text-xs" onClick={() => setListMode("active")}>
              Aktif
            </Button>
            <Button type="button" variant={listMode === "archive" ? "default" : "outline"} className="h-8 rounded-full px-3 text-xs" onClick={() => setListMode("archive")}>
              Arşiv
            </Button>
          </div>
          <div className="space-y-2">
            {ticketsQuery.isLoading ? <div className="p-2 text-sm text-slate-500">Yükleniyor...</div> : null}
            {ticketsQuery.isError ? <div className="p-2 text-sm text-rose-600">{(ticketsQuery.error as Error).message}</div> : null}
            {!ticketsQuery.isLoading && !visibleTickets.length ? <div className="p-2 text-sm text-slate-500">{listMode === "archive" ? "Arşivde ticket yok." : "Henüz ticket yok."}</div> : null}
            {visibleTickets.map((ticket) => (
              <button
                key={ticket.id}
                type="button"
                onClick={() => setSelectedId(ticket.id)}
                className={cn(
                  "w-full rounded-xl border p-3 text-left transition-colors",
                  selectedId === ticket.id ? "border-[var(--panel-primary)] bg-[var(--panel-primary)]/5" : "border-slate-200 hover:bg-slate-50",
                )}
              >
                <div className="line-clamp-1 text-sm font-semibold">#{ticket.ticketNo} • {ticket.subject}</div>
                <div className="mt-1 text-xs text-slate-500">{ticket.status === "archived" ? "Arşivde" : ticket.status === "closed" ? "Kapalı" : "Açık"} • {formatDate(ticket.updatedAt)}</div>
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-4">
          {!selectedId ? (
            <div className="text-sm text-slate-500">Detay görmek için soldan bir ticket seç.</div>
          ) : detailQuery.isLoading ? (
            <div className="text-sm text-slate-500">Mesajlar yükleniyor...</div>
          ) : detailQuery.isError ? (
            <div className="text-sm text-rose-600">{(detailQuery.error as Error).message}</div>
          ) : selectedTicket ? (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-3">
                <h2 className="text-lg font-bold">#{selectedTicket.ticketNo} • {selectedTicket.subject}</h2>
                <div className="flex items-center gap-2">
                  <span className={cn("rounded-full px-3 py-1 text-xs font-semibold", selectedTicket.status === "archived" ? "bg-violet-100 text-violet-700" : selectedTicket.status === "closed" ? "bg-slate-200 text-slate-700" : "bg-emerald-100 text-emerald-700")}>{selectedTicket.status === "archived" ? "Arşivde" : selectedTicket.status === "closed" ? "Kapalı" : "Açık"}</span>
                  {selectedTicket.status !== "archived" ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-8 rounded-full px-3 text-xs"
                      disabled={statusMutation.isPending}
                      onClick={() => statusMutation.mutate({ id: selectedTicket.id, status: selectedTicket.status === "closed" ? "open" : "closed" })}
                    >
                      {selectedTicket.status === "closed" ? "Yeniden Aç" : "Ticketi Kapat"}
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 rounded-full px-3 text-xs"
                    disabled={statusMutation.isPending}
                    onClick={() => statusMutation.mutate({ id: selectedTicket.id, status: selectedTicket.status === "archived" ? "open" : "archived" })}
                  >
                    {selectedTicket.status === "archived" ? "Arşivden Çıkar" : "Arşive Al"}
                  </Button>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                {selectedTicket.messages.map((item) => (
                  <div key={item.id} className={cn("max-w-[90%] rounded-xl p-3 text-sm", item.actor === "admin" ? "border border-indigo-200 bg-indigo-50" : "border border-slate-200 bg-slate-50")}>
                    <div className="mb-1 text-xs font-semibold text-slate-500">{item.senderName} • {formatDate(item.createdAt)}</div>
                    <p className="whitespace-pre-wrap">{item.message}</p>
                  </div>
                ))}
              </div>

              <div className="mt-4 border-t pt-4">
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Yanıt yaz"
                  className="min-h-[90px]"
                />
                <div className="mt-3 flex justify-end">
                  <Button
                    disabled={replyMutation.isPending || selectedTicket.status !== "open"}
                    onClick={() => replyMutation.mutate({ id: selectedTicket.id, message: reply.trim() })}
                  >
                    {replyMutation.isPending ? "Gönderiliyor..." : selectedTicket.status === "archived" ? "Ticket Arşivde" : selectedTicket.status === "closed" ? "Ticket Kapalı" : "Yanıt Gönder"}
                  </Button>
                </div>
                {replyMutation.isError ? <p className="mt-2 text-sm text-rose-600">{(replyMutation.error as Error).message}</p> : null}
                {statusMutation.isError ? <p className="mt-2 text-sm text-rose-600">{(statusMutation.error as Error).message}</p> : null}
              </div>
            </div>
          ) : null}
        </Card>
      </div>}
    </div>
  );
}
